import type { ReactNode } from 'react';
import './Timeline.css';

/**
 * Timeline — **지나온 일** (정본 E9).
 *
 * ★ `Stepper` 와 다르다 — Stepper 는 **앞으로 갈 길**, Timeline 은 **지나온 일**이다.
 * ★★ **최신이 위**다(이력은 「방금 무슨 일이 있었나」를 먼저 본다).
 *    ⚠ 정본이 적어 둔 예외 하나 — **시각 축 옆에 서는 이력**(근태의 00~24시 띠 옆)은
 *      오름차순이다. 띠는 왼쪽부터 이른 시각이라 목록만 뒤집으면 두 그림이 반대로 읽힌다.
 *      그런 자리는 이 부품이 아니라 한 줄짜리 행을 쓴다. **홀로 서는 이력만 여기다.**
 * ★ 치수는 정본 그대로다 — 점 10px · 잇는 선 2px(`--border`) · **마지막 항목엔 선을 안 그린다** ·
 *   시각은 `--font-num`(자리가 흔들리면 안 되는 칸).
 * ★ 뼈대는 `<ol>` + `<time dateTime>` — 「차례가 있는 목록」이고 시각은 기계도 읽는다.
 */

export type TimelineTone = 'success' | 'info' | 'warning' | 'danger' | 'muted';

export interface TimelineItem {
  id: string;
  /** 무슨 일이 있었나 — 한 줄. */
  title: ReactNode;
  /** 언제. ISO 를 주면 `<time dateTime>` 이 되고, 보이는 글자는 `label` 이다. */
  at?: string;
  /** 시각을 사람 말로. 없으면 `at` 을 그대로 적는다. */
  atLabel?: string;
  /** 덧붙일 말(사유 등) — 있으면 제목 아래 한 줄. */
  meta?: ReactNode;
  tone?: TimelineTone;
}

export function Timeline({ items, className }: { items: readonly TimelineItem[]; className?: string }) {
  return (
    <ol className={['wc-timeline', className ?? ''].filter(Boolean).join(' ')}>
      {items.map((it, i) => (
        <li className="wc-timeline__row" key={it.id}>
          <span className="wc-timeline__rail" aria-hidden>
            <span className={`wc-timeline__dot wc-timeline__dot--${it.tone ?? 'muted'}`} />
            {/* ★ 마지막 항목엔 선을 그리지 않는다 — 아래로 이어지는 것이 없기 때문이다. */}
            {i < items.length - 1 && <span className="wc-timeline__line" />}
          </span>
          <span className="wc-timeline__body">
            <span className="wc-timeline__title">{it.title}</span>
            {it.meta != null && <span className="wc-timeline__meta">{it.meta}</span>}
            {(it.atLabel ?? it.at) != null && (
              <time className="wc-timeline__at" dateTime={it.at}>{it.atLabel ?? it.at}</time>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}
